/* Fire + Ice — Fast Track landing page (templates/page.fs-lp-fireice-fast-track).
   Shared behaviour for sections/section-fi-ft-*.liquid:
   - smooth in-page anchors with the 72px sticky-header offset (instant under
     prefers-reduced-motion); focus moves to the target section;
   - muted-autoplay nudge for the opportunity film (paused under reduced motion);
   - the booking section: location cards and the application form share one
     selection (location + session), session availability (Full / Closed,
     closesAt re-checked at load, ?ft_test=1 reveals test sessions), client
     validation (ticking the "Other" treatment reveals a required text box),
     and the pay-first submit — POST JSON to the app proxy, then send the
     applicant to the checkout URL it returns. The team reviews paid
     applications in the HGI Business app.
   Every fi-ft section renders the same deferred script tag; the guard stops it
   binding twice. Theme-editor section reloads re-run init() on the new markup. */
(function () {
  'use strict';

  if (window.__fiFtInit) return;
  window.__fiFtInit = true;

  var HEADER_OFFSET = 72;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var forEach = Array.prototype.forEach;

  function reducedMotion() {
    return !!(motion && motion.matches);
  }

  function queryParam(name) {
    try {
      return new URLSearchParams(window.location.search).get(name);
    } catch (e) {
      return null;
    }
  }

  function makeOption(value, label, disabled, selected) {
    var option = document.createElement('option');
    option.value = value;
    option.textContent = label || '';
    option.disabled = !!disabled;
    if (selected) {
      option.defaultSelected = true;
      option.selected = true;
    }
    return option;
  }

  function empty(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }

  /* Only follow a same-site path or an https URL from the app. */
  function safeUrl(url) {
    return typeof url === 'string' && (/^\/(?!\/)/.test(url) || /^https:\/\//i.test(url));
  }

  function moneyFormatter(currency) {
    return function (cents) {
      var value = Number(cents);
      if (!isFinite(value)) return '';
      var digits = Math.round(value) % 100 === 0 ? 0 : 2;
      try {
        return new Intl.NumberFormat('en-GB', {
          style: 'currency',
          currency: currency || 'GBP',
          minimumFractionDigits: digits,
          maximumFractionDigits: digits
        }).format(value / 100);
      } catch (e) {
        return (value / 100).toFixed(digits);
      }
    };
  }

  /* ---- In-page anchors: offset for the sticky header, then focus the target ---- */
  function scrollToTarget(target) {
    if (!target) return;
    var top = target.getBoundingClientRect().top + window.pageYOffset - HEADER_OFFSET;
    window.scrollTo({ top: Math.max(0, Math.round(top)), behavior: reducedMotion() ? 'auto' : 'smooth' });
    if (target.getAttribute('tabindex') === '-1') target.focus({ preventScroll: true });
  }

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest ? event.target.closest('a[href^="#"]') : null;
    if (!link || !link.closest('.fi-ft')) return;
    var id = link.getAttribute('href').slice(1);
    var target = id ? document.getElementById(id) : null;
    if (!target) return;
    event.preventDefault();
    scrollToTarget(target);
  });

  /* ---- Opportunity film: make sure the muted loop actually starts ---- */
  function play(video) {
    var playing = video.play();
    if (playing && typeof playing.catch === 'function') playing.catch(function () {});
  }

  /* Sound button: browsers only autoplay muted, so the visitor turns sound on.
     Unmuting happens inside the click, which is what iOS needs; it also starts
     a film left paused (reduced motion or a blocked autoplay). */
  function initSound(video) {
    var button = video.parentNode ? video.parentNode.querySelector('[data-fi-ft-sound]') : null;
    if (!button) return;
    var label = button.querySelector('[data-fi-ft-sound-label]');
    var paint = function () {
      var on = !video.muted;
      var text = on ? button.getAttribute('data-label-off') : button.getAttribute('data-label-on');
      button.classList.toggle('is-on', on);
      if (label) label.textContent = text || '';
      button.setAttribute('aria-label', text || '');
    };
    button.addEventListener('click', function () {
      video.muted = !video.muted;
      if (!video.muted) {
        video.volume = 1;
        if (video.paused) play(video);
      }
      paint();
    });
    video.addEventListener('volumechange', paint);
    paint();
    button.hidden = false;
  }

  function initVideos(scope) {
    forEach.call(scope.querySelectorAll('.fi-ft video'), function (video) {
      if (video.hasAttribute('data-fi-ft-video')) return;
      video.setAttribute('data-fi-ft-video', '');
      video.muted = true;
      video.defaultMuted = true;
      initSound(video);
      if (reducedMotion()) {
        video.removeAttribute('autoplay');
        video.pause();
        return;
      }
      play(video);
    });
  }

  /* ---- Booking: location cards + application form ---- */
  function initBooking(root) {
    if (root.hasAttribute('data-fi-ft-ready')) return;
    root.setAttribute('data-fi-ft-ready', '');

    var data = {};
    var dataEl = root.querySelector('script[data-fi-ft-data]');
    try {
      data = JSON.parse(dataEl ? dataEl.textContent : '{}') || {};
    } catch (e) {
      data = {};
    }

    var strings = data.strings || {};
    var variants = data.variants || {};
    var endpoint = data.endpoint || '/apps/hgi-business/fast-track/apply';
    var formatMoney = moneyFormatter(data.currency);
    var testMode = queryParam('ft_test') === '1';
    /* Never trust a client clock that is behind the server render. */
    var now = Math.max(Date.now(), (Number(data.renderedAt) || 0) * 1000);

    var locations = [];
    var locationByKey = {};
    var sessionById = {};

    function statusOf(raw, variant) {
      if (!variant) return 'closed';
      if (variant.available === false || !(Number(variant.inventory) > 0)) return 'full';
      if (raw.open === false) return 'closed';
      var closes = raw.closesAt ? Date.parse(raw.closesAt) : NaN;
      if (!isNaN(closes) && closes <= now) return 'closed';
      return '';
    }

    (Array.isArray(data.locations) ? data.locations : []).forEach(function (rawLocation) {
      var key = rawLocation && rawLocation.key != null ? String(rawLocation.key) : '';
      if (!key) return;
      var loc = { key: key, city: String(rawLocation.city || key), sessions: [] };
      (Array.isArray(rawLocation.sessions) ? rawLocation.sessions : []).forEach(function (raw) {
        var id = raw && raw.id != null ? String(raw.id) : '';
        if (!id || (raw.test && !testMode)) return;
        var variant = variants[String(raw.variantId)] || null;
        var session = {
          id: id,
          variantId: raw.variantId,
          label: String(raw.label || raw.date || ''),
          location: loc,
          price: variant && isFinite(Number(variant.price)) ? Number(variant.price) : null,
          spaces: variant ? Math.max(0, Math.floor(Number(variant.inventory) || 0)) : 0,
          status: statusOf(raw, variant)
        };
        loc.sessions.push(session);
        sessionById[session.id] = session;
      });
      if (!loc.sessions.length) return;
      locations.push(loc);
      locationByKey[loc.key] = loc;
    });

    function bookable(loc) {
      return loc.sessions.some(function (s) { return !s.status; });
    }

    function openSpaces(loc) {
      return loc.sessions.reduce(function (sum, s) { return s.status ? sum : sum + s.spaces; }, 0);
    }

    function statusText(session) {
      if (session.status === 'full') return strings.statusFull || '';
      if (session.status === 'closed') return strings.statusClosed || '';
      return '';
    }

    function spacesText(count) {
      if (count <= 0) return strings.spacesNone || '';
      return String((count === 1 ? strings.spacesOne : strings.spacesMany) || '').replace('[count]', count);
    }

    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-fi-ft-location]'));
    var applySection = root.querySelector('[data-fi-ft-apply]');
    var form = root.querySelector('[data-fi-ft-form]');
    var fields = {};
    if (form) {
      forEach.call(form.querySelectorAll('[data-fi-ft-field]'), function (el) {
        fields[el.getAttribute('data-fi-ft-field')] = el;
      });
    }
    var locationSelect = fields.location || null;
    var dateSelect = fields.date || null;
    var submitButton = form ? form.querySelector('[data-fi-ft-submit]') : null;
    var errorBox = form ? form.querySelector('[data-fi-ft-error]') : null;
    var otherToggle = form ? form.querySelector('[data-fi-ft-other-toggle]') : null;
    var otherField = form ? form.querySelector('[data-fi-ft-other-field]') : null;

    var state = { location: null, session: null };
    var busy = false;
    var redirecting = false;
    var dateOptionsFor = null;

    function buildLocationOptions() {
      if (!locationSelect) return;
      empty(locationSelect);
      locationSelect.appendChild(makeOption('', strings.locationPlaceholder, true, true));
      locations.forEach(function (loc) {
        var open = bookable(loc);
        locationSelect.appendChild(makeOption(loc.key, open ? loc.city : loc.city + ' · ' + (strings.spacesNone || ''), !open, false));
      });
    }

    function syncDateSelect() {
      if (!dateSelect) return;
      var loc = state.location ? locationByKey[state.location] || null : null;
      var key = loc ? loc.key : '';
      if (dateOptionsFor !== key) {
        dateOptionsFor = key;
        empty(dateSelect);
        dateSelect.appendChild(makeOption('', loc ? strings.datePlaceholder : strings.datePlaceholderEmpty, true, true));
        if (loc) {
          loc.sessions.forEach(function (s) {
            dateSelect.appendChild(makeOption(s.id, s.status ? s.label + ' · ' + statusText(s) : s.label, !!s.status, false));
          });
        }
        dateSelect.disabled = !loc;
      }
      var session = state.session ? sessionById[state.session] : null;
      dateSelect.value = session && session.location === loc ? session.id : '';
    }

    function paintCard(card) {
      var loc = locationByKey[card.getAttribute('data-fi-ft-location')];
      if (!loc) return;
      var selected = state.location === loc.key;
      var session = selected && state.session ? sessionById[state.session] : null;
      card.classList.toggle('is-selected', selected);

      var selectButton = card.querySelector('[data-fi-ft-select]');
      var continueButton = card.querySelector('[data-fi-ft-continue]');
      if (selectButton) {
        selectButton.hidden = selected;
        selectButton.disabled = !bookable(loc);
      }
      if (continueButton) continueButton.hidden = !selected;

      forEach.call(card.querySelectorAll('[data-fi-ft-session]'), function (button) {
        var pressed = !!session && session.id === button.getAttribute('data-fi-ft-session');
        button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
      });

      var spaces = card.querySelector('[data-fi-ft-spaces]');
      if (spaces) {
        var text = spacesText(session && session.location === loc ? session.spaces : openSpaces(loc));
        if (spaces.textContent !== text) spaces.textContent = text;
      }
    }

    /* The booking fee in the note under the button follows the chosen date. */
    function paintSubmit() {
      var session = state.session ? sessionById[state.session] : null;
      var price = session && session.price !== null ? session.price : data.basePrice;
      var priceText = price == null ? '' : formatMoney(price);
      forEach.call(root.querySelectorAll('[data-fi-ft-price]'), function (el) {
        if (priceText) el.textContent = priceText;
      });
      if (!submitButton) return;
      var label = redirecting && strings.redirecting
        ? String(strings.redirecting)
        : busy && strings.sending ? String(strings.sending) : String(strings.submit || '');
      submitButton.textContent = priceText ? label.replace('[price]', priceText) : label.replace(/\s*\[price\]/, '');
    }

    function render() {
      /* No usable session data: leave the server-rendered markup as it is. */
      if (!locations.length) return;
      cards.forEach(paintCard);
      if (locationSelect) {
        locationSelect.value = state.location || '';
        if (state.location) clearInvalid(locationSelect);
      }
      syncDateSelect();
      if (dateSelect && state.session) clearInvalid(dateSelect);
      paintSubmit();
    }

    /* The "Other" treatment reveals its text box. */
    function syncOther(focus) {
      if (!otherToggle || !otherField) return;
      var open = otherToggle.checked;
      otherField.hidden = !open;
      if (!open) {
        clearInvalid(fields.treatmentsOther);
        hideError();
      } else if (focus && fields.treatmentsOther) {
        fields.treatmentsOther.focus();
      }
    }

    function toggleAddress(button) {
      var open = button.getAttribute('aria-expanded') !== 'true';
      var panel = document.getElementById(button.getAttribute('aria-controls'));
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (panel) panel.hidden = !open;
    }

    /* ---- Errors ---- */
    function showError(message, els) {
      if (errorBox) {
        errorBox.textContent = message || '';
        errorBox.hidden = false;
      }
      (els || []).forEach(function (el) {
        if (!el) return;
        el.setAttribute('aria-invalid', 'true');
        if (errorBox && errorBox.id) el.setAttribute('aria-describedby', errorBox.id);
      });
    }

    function hideError() {
      if (!errorBox || errorBox.hidden) return;
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    function clearInvalid(el) {
      if (!el || !el.hasAttribute('aria-invalid')) return;
      el.removeAttribute('aria-invalid');
      el.removeAttribute('aria-describedby');
    }

    function clearAllInvalid() {
      if (form) forEach.call(form.querySelectorAll('[aria-invalid]'), clearInvalid);
    }

    /* Map the app's `fields` keys onto form controls (it reports the session as "date"). */
    function fieldEls(map) {
      var els = [];
      if (!map || typeof map !== 'object') return els;
      Object.keys(map).forEach(function (key) {
        var el = fields[key] || (key === 'sessionId' || key === 'variantId' ? dateSelect : null);
        if (el && els.indexOf(el) === -1) els.push(el);
      });
      return els;
    }

    /* The app says the date sold out or closed since the page loaded: show it. */
    function closeSession(session, status) {
      if (!session) return;
      session.status = status;
      forEach.call(root.querySelectorAll('[data-fi-ft-session]'), function (button) {
        if (button.getAttribute('data-fi-ft-session') !== session.id) return;
        button.disabled = true;
        var label = button.querySelector('[data-fi-ft-status]');
        if (label) label.textContent = statusText(session);
      });
      if (state.session === session.id) state.session = null;
      dateOptionsFor = null;
      buildLocationOptions();
      render();
    }

    /* ---- Submit ---- */
    function value(key) {
      var el = fields[key];
      return el && typeof el.value === 'string' ? el.value.trim() : '';
    }

    function setBusy(on) {
      busy = on;
      if (!submitButton) return;
      submitButton.disabled = on;
      if (on) submitButton.setAttribute('aria-busy', 'true');
      else submitButton.removeAttribute('aria-busy');
      paintSubmit();
    }

    /* Swap the heading and form for the "Application received" panel. */
    function showDone(session, ref) {
      var done = root.querySelector('[data-fi-ft-done]');
      if (!done || !form) return;
      var head = root.querySelector('[data-fi-ft-apply-head]');
      var summary = done.querySelector('[data-fi-ft-done-summary]');
      var refLine = done.querySelector('[data-fi-ft-done-ref]');
      if (summary) summary.textContent = session ? session.location.city + ' · ' + session.label : '';
      if (refLine && ref && strings.reference) {
        refLine.textContent = String(strings.reference).replace('[ref]', String(ref));
        refLine.hidden = false;
      }
      if (head) head.hidden = true;
      form.hidden = true;
      done.hidden = false;
      scrollToTarget(applySection);
      var heading = done.querySelector('[data-fi-ft-done-heading]');
      if (heading) heading.focus({ preventScroll: true });
    }

    function send(payload, session) {
      setBusy(true);
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      })
        .then(function (response) {
          return response.json().then(
            function (body) { return { ok: response.ok, body: body }; },
            function () { return { ok: response.ok, body: null }; }
          );
        })
        .then(function (result) {
          var body = result.body;
          if (result.ok && body && body.ok === true && safeUrl(body.checkoutUrl)) {
            redirecting = true;
            paintSubmit();
            window.location.assign(body.checkoutUrl);
            return;
          }
          setBusy(false);
          if (result.ok && body && body.ok === true) {
            showDone(session, body.ref);
            return;
          }
          if (body && body.ok === false && body.error) {
            if (body.code === 'SESSION_FULL') closeSession(session, 'full');
            if (body.code === 'SESSION_CLOSED') closeSession(session, 'closed');
            showError(String(body.error), fieldEls(body.fields));
          } else {
            showError(strings.network, []);
          }
        })
        .catch(function () {
          setBusy(false);
          showError(strings.network, []);
        });
    }

    function onSubmit(event) {
      event.preventDefault();
      if (busy) return;

      var missing = [];
      var invalid = [];
      forEach.call(form.querySelectorAll('[data-fi-ft-required]'), function (el) {
        var key = el.getAttribute('data-fi-ft-field');
        var current = key === 'location' ? state.location : key === 'date' ? state.session : el.value;
        if (!String(current || '').trim()) {
          missing.push(el.getAttribute('data-fi-ft-required'));
          invalid.push(el);
        }
      });

      clearAllInvalid();
      if (missing.length) {
        showError(String(strings.required || '').replace('[fields]', missing.join(', ')), invalid);
        return;
      }

      var email = value('email');
      if (!EMAIL_RE.test(email)) {
        showError(strings.invalidEmail, [fields.email]);
        return;
      }

      var otherTicked = !!(otherToggle && otherToggle.checked);
      if (otherTicked && fields.treatmentsOther && !value('treatmentsOther')) {
        showError(strings.otherRequired, [fields.treatmentsOther]);
        return;
      }

      var session = state.session ? sessionById[state.session] : null;
      if (!session || session.status) {
        showError(String(strings.required || '').replace('[fields]', dateSelect ? dateSelect.getAttribute('data-fi-ft-required') : ''), [dateSelect]);
        return;
      }

      hideError();
      var treatments = [];
      forEach.call(form.querySelectorAll('input[name="treatments"]'), function (box) {
        if (box.checked) treatments.push(box.value);
      });
      var honeypot = form.querySelector('input[name="hp"]');
      var variantId = Number(session.variantId);

      send({
        sessionId: session.id,
        variantId: isFinite(variantId) && variantId > 0 ? variantId : session.variantId,
        firstName: value('firstName'),
        lastName: value('lastName'),
        email: email,
        mobile: value('mobile'),
        businessName: value('businessName'),
        website: value('website'),
        businessAddress: value('businessAddress'),
        postcode: value('postcode'),
        businessType: value('businessType'),
        qualificationLevel: value('qualificationLevel'),
        treatments: treatments,
        treatmentsOther: otherTicked ? value('treatmentsOther') : '',
        notes: value('notes'),
        sourcePageUrl: window.location.href,
        hp: honeypot ? honeypot.value : ''
      }, session);
    }

    /* ---- Initial paint: availability as of now, test sessions, select options ---- */
    if (locations.length) {
      cards.forEach(function (card) {
        var loc = locationByKey[card.getAttribute('data-fi-ft-location')];
        card.hidden = !loc;
        if (!loc) return;
        forEach.call(card.querySelectorAll('[data-fi-ft-session]'), function (button) {
          var session = sessionById[button.getAttribute('data-fi-ft-session')];
          button.hidden = !session;
          if (!session) return;
          button.disabled = !!session.status;
          var label = button.querySelector('[data-fi-ft-status]');
          if (label) label.textContent = statusText(session);
        });
      });
      /* Keep a location picked before this deferred script ran. */
      var picked = locationSelect ? locationByKey[locationSelect.value] : null;
      if (picked && bookable(picked)) state.location = picked.key;
      buildLocationOptions();
    }
    render();
    syncOther(false);

    /* ---- Events ---- */
    root.addEventListener('click', function (event) {
      var button = event.target.closest ? event.target.closest('button') : null;
      if (!button || !root.contains(button) || button.disabled) return;

      if (button.hasAttribute('data-fi-ft-address-toggle')) {
        toggleAddress(button);
        return;
      }

      var card = button.closest('[data-fi-ft-location]');
      var loc = card ? locationByKey[card.getAttribute('data-fi-ft-location')] : null;
      if (!loc) return;

      if (button.hasAttribute('data-fi-ft-session')) {
        var session = sessionById[button.getAttribute('data-fi-ft-session')];
        if (!session || session.status) return;
        var pressed = state.location === loc.key && state.session === session.id;
        state.location = loc.key;
        state.session = pressed ? null : session.id;
        hideError();
        render();
      } else if (button.hasAttribute('data-fi-ft-select')) {
        if (!bookable(loc)) return;
        state.location = loc.key;
        state.session = null;
        hideError();
        render();
        scrollToTarget(applySection);
      } else if (button.hasAttribute('data-fi-ft-continue')) {
        scrollToTarget(applySection);
      }
    });

    if (locationSelect) {
      locationSelect.addEventListener('change', function () {
        var loc = locationByKey[locationSelect.value];
        state.location = loc && bookable(loc) ? loc.key : null;
        state.session = null;
        hideError();
        render();
      });
    }

    if (dateSelect) {
      dateSelect.addEventListener('change', function () {
        var session = sessionById[dateSelect.value];
        state.session = session && !session.status && session.location.key === state.location ? session.id : null;
        hideError();
        render();
      });
    }

    if (form) {
      var onEdit = function (event) {
        var el = event.target;
        if (!el || el === locationSelect || el === dateSelect || el.type === 'checkbox' || el.name === 'hp') return;
        clearInvalid(el);
        hideError();
      };
      form.addEventListener('input', onEdit);
      form.addEventListener('change', onEdit);
      form.addEventListener('submit', onSubmit);
      if (otherToggle) {
        otherToggle.addEventListener('change', function () { syncOther(true); });
      }
    }

    /* Back from checkout (bfcache): the button must not stay on "Taking you to secure payment…". */
    window.addEventListener('pageshow', function (event) {
      if (!event.persisted || !(busy || redirecting)) return;
      redirecting = false;
      setBusy(false);
    });
  }

  function init(scope) {
    var root = scope && scope.querySelectorAll ? scope : document;
    forEach.call(root.querySelectorAll('[data-fi-ft-booking]'), initBooking);
    initVideos(root);
  }

  init(document);
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
